import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { IEmotionBubble, emotionBubbleStylesMapped } from "../../../models/interactions/EmotionBubble";
import Interaction from "../../../models/interactions/Interaction";
import NodeActorPropDropdown from "../components/NodeActorPropDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import MessageBubbleAnimationDropdown from "../../previews/message_bubble/MessageBubbleAnimationDropdown";
import LookatTargetDropdown from "./LookatTargetDropdown";
import usePropHelper from "../../../hooks/PropHelper";

export default function EmotionBubbleNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const emotionBubble = props.interaction as IEmotionBubble;
  const { updateInteraction, sequence } = useUpdateSequenceInteraction();
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps } = usePropHelper();
  const propsInTake = useMemo(
    () =>
      take
        ? getBubbleTargetProps(take, {
            kind: "sequence",
            nodeId: props.nodeId,
            sequence,
          })
        : [],
    [getBubbleTargetProps, props.nodeId, sequence, take]
  );
  const isPropIdInTake = (objectId: string) =>
    propsInTake.some((prop) => prop.id === objectId);
  const isPropAnchor = isPropIdInTake(emotionBubble.npcId);

  useEffect(() => {
    if (!isPropAnchor || !emotionBubble.lookAt) return;
    updateInteraction(props.nodeId, {
      ...emotionBubble,
      lookAt: "",
    } as IEmotionBubble);
  }, [emotionBubble, isPropAnchor, props.nodeId, updateInteraction]);

  const actorDropdown = useMemo(
    () => (
      <NodeActorPropDropdown
        label="Actor"
        selectedObjectId={emotionBubble.npcId}
        availableProps={propsInTake}
        onClick={(objectId: string) => {
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            npcId: objectId,
            lookAt: isPropIdInTake(objectId) ? "" : emotionBubble.lookAt,
          } as IEmotionBubble);
        }}
      />
    ),
    [emotionBubble, propsInTake, updateInteraction, props.nodeId]
  );

  const lookAtDropdown = useMemo(
    () => (
      <LookatTargetDropdown
        label="Look at"
        nodeId={props.nodeId}
        selectedTargetId={emotionBubble.lookAt}
        allowNoTarget
        noTargetLabel="No actor"
        disabled={isPropAnchor}
        onClick={(targetId: string) =>
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            lookAt: targetId,
          } as IEmotionBubble)
        }
      />
    ),
    [isPropAnchor, emotionBubble, updateInteraction, props.nodeId]
  );

  const animationDropdown = useMemo(
    () => (
      <MessageBubbleAnimationDropdown
        selectedAnimation={emotionBubble.animation}
        variant="node"
        onClick={(animation) =>
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            animation: animation,
          } as IEmotionBubble)
        }
      />
    ),
    [emotionBubble.animation, updateInteraction, props.nodeId]
  );

  const emotionStyleDropdown = useMemo(
    () => (
      <NodeStringDropdown
        label="Emotion Style"
        items={emotionBubbleStylesMapped.map(style => style.name)}
        selectedItem={emotionBubble.messageBubbleStyle}
        onClick={(style: string) =>
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            messageBubbleStyle: style,
          } as IEmotionBubble)
        }
      />
    ),
    [emotionBubble.messageBubbleStyle, updateInteraction, props.nodeId]
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {lookAtDropdown}
      {animationDropdown}
      {emotionStyleDropdown}
    </div>
  );
}
